import * as React from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { useOutletContext } from 'react-router-dom'

import { InfiniteScroll } from '@factorialco/deprecated-design-system'
import classnames from 'classnames/bind'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Loading from 'modules/core/components/Loading'

import type ReportStruct from 'modules/reports/lib/ReportStruct'
import type { RouteContextParams } from 'modules/reports/lib/RouterContextParams'
import type Template from 'modules/reports/store/models/Template'

import Item from './Item'
import { DASHBOARD_COLS, generateLayout } from './generateLayout'
import styles from './index.module.scss'

const cx = classnames.bind(styles)
const ResponsiveReactGridLayout = WidthProvider(Responsive)
interface Props {
  templates: ReportStruct<Template>[]
}

const ENTRIES_PER_PAGE = 10

export default function List({ templates }: Props) {
  const [initialLoad, setInitialLoad] = React.useState(true)
  const [page, setPage] = React.useState(1)

  const [hasMore, setHasMore] = React.useState(templates.length > ENTRIES_PER_PAGE)

  const { dashboard } = useOutletContext<RouteContextParams>()

  const loadMore = (newPage: number) => {
    // https://github.com/danbovey/react-infinite-scroller/issues/163#issuecomment-411201250
    setInitialLoad(false)
    setPage(newPage)
    setHasMore(templates.length > ENTRIES_PER_PAGE * newPage)
  }

  const templatesToShow = () => templates.slice(0, ENTRIES_PER_PAGE * page)

  // When the user changes any filter (searchbar or category), it is
  // necessary to reset the pagination
  React.useEffect(() => {
    setPage(1)
    setHasMore(true)
    setInitialLoad(true)
  }, [templates])

  const rows = templatesToShow()
  const layout = generateLayout(rows)

  return (
    <ErrorBoundary>
      <InfiniteScroll
        initialLoad={initialLoad}
        pageStart={page}
        loadMore={loadMore}
        hasMore={hasMore}
        loader={
          <div key={`infiniteLoader-${page}`}>
            <Loading />
          </div>
        }
      >
        <ResponsiveReactGridLayout
          className={styles.chartList}
          cols={{
            lg: DASHBOARD_COLS,
            md: DASHBOARD_COLS,
            sm: DASHBOARD_COLS,
            xs: DASHBOARD_COLS,
            xxs: DASHBOARD_COLS,
          }}
          rowHeight={215}
          margin={[32, 24]}
          containerPadding={[0, 0]}
          layouts={layout as any}
        >
          {rows.map((template) => (
            <div key={template.templateSlug} className={cx('cell')}>
              {dashboard && <Item reportStruct={template} dashboard={dashboard} />}
            </div>
          ))}
        </ResponsiveReactGridLayout>
      </InfiniteScroll>
    </ErrorBoundary>
  )
}
